import { supabase } from '../supabaseClient';

/* ══════════════════════════════════════════════════════════════════════════
   OLVASÓ (segédletek az appban, 1. kör) — adat és tiszta segédfüggvények
   ─────────────────────────────────────────────────────────────────────────
   A tartalom lapról lapra jön a szerverről (landing migráció 20261116120000_olvaso_1):
     olvaso_lista(), olvaso_tartalom(slug), olvaso_lap(slug, idx), olvaso_hiba(...).
   A jogosultság, a navigáció, a korlát és a jelölés szerveroldali; itt csak a
   megjelenítés logikája van, hogy Jesttel tesztelhető legyen.
   ══════════════════════════════════════════════════════════════════════════ */

export async function olvasoLista() {
  const { data, error } = await supabase.rpc('olvaso_lista');
  if (error) throw error;
  return data || [];
}

/* A lista egyszer töltődik oldalbetöltésenként (a modulkártyák mind ezt használják). */
let listaPromise = null;
export function olvasoListaCached() {
  if (!listaPromise) listaPromise = olvasoLista().catch(() => { listaPromise = null; return []; });
  return listaPromise;
}

export async function olvasoTartalom(slug) {
  const { data, error } = await supabase.rpc('olvaso_tartalom', { p_slug: slug });
  if (error) throw error;
  return data;
}

export async function olvasoLap(slug, idx) {
  const { data, error } = await supabase.rpc('olvaso_lap', { p_slug: slug, p_idx: idx });
  if (error) throw error;
  return data;
}

/* 2. kör: a szabad anyag kijelentkezve (IP-lenyomatos korlát, haladás és jelölés nélkül). */
export async function olvasoLapNyilvanos(slug, idx) {
  const { data, error } = await supabase.rpc('olvaso_lap_nyilvanos', { p_slug: slug, p_idx: idx });
  if (error) throw error;
  return data;
}

/* A lapbetöltési hiba a hibanaplóba (function_errors); ez maga sosem dob. */
export function olvasoHiba(slug, idx, uzenet) {
  return supabase.rpc('olvaso_hiba', { p_slug: slug, p_idx: idx, p_uzenet: String(uzenet || '').slice(0, 200) })
    .then(() => undefined, () => undefined);
}

export const API = { tartalom: olvasoTartalom, lap: olvasoLap, hiba: olvasoHiba };
/* 3. kör: a PDF-jog és a letöltés (a modulkártyák közös útja) */
export { pdfAllapot, resourceUrl, limitMessage, pdfButtonLabel } from './moduleResources';
/* Kijelentkezve: a lap a nyilvános úton (csak a szabad anyag), a hiba nem naplózható (nincs fiók). */
export const API_NYILVANOS = { tartalom: olvasoTartalom, lap: olvasoLapNyilvanos, hiba: () => Promise.resolve() };

export const LEVEL_LABEL = { szabad: 'SZABAD', ingyenes: 'INGYENES', tag: 'TAG', plusz: 'PLUSZ', pro: 'PRO' };

/* ── Lapozás ── */

export const SWIPE_MIN_PX = 60;
export const SWIPE_RATIO = 1.5;

/** Oldalra húzás: +1 (következő), −1 (előző) vagy 0 (nem lapozás, pl. görgetés). */
export function swipeDirection(dx, dy) {
  if (Math.abs(dx) <= SWIPE_MIN_PX) return 0;
  if (Math.abs(dx) <= Math.abs(dy) * SWIPE_RATIO) return 0;
  return dx < 0 ? 1 : -1;
}

/** Az index a lapok határain belül. */
export const clampIdx = (i, n) => Math.max(0, Math.min(n - 1, i));

/** A lap nagyítása a nyomtatott méretekhez (a prototípus szerint). */
export const zoomFor = (width) => (width >= 760 ? 1.18 : 1.32);

/* ── Tartalomjegyzék és haladás ── */

/** Fejezetekre bontva, lapsorrendben: [{ fejezet, lapok: [{idx, cim}] }]. Üres fejezet ("") is csoport,
    csak a felület nem ír fölé címet (egyfejezetes anyag). */
export function groupToc(tartalom) {
  const out = [];
  (tartalom || []).forEach(l => {
    const last = out[out.length - 1];
    if (last && last.fejezet === l.fejezet) last.lapok.push({ idx: l.idx, cim: l.cim });
    else out.push({ fejezet: l.fejezet, lapok: [{ idx: l.idx, cim: l.cim }] });
  });
  return out;
}

/** „x / y elolvasva” számai (a látott lapok egyedi száma). */
export const seenCount = (latott) => new Set(latott || []).size;

/** Haladás százalékban (a látott lapok aránya). */
export const progressPct = (latott, n) => (n ? Math.round((seenCount(latott) / n) * 100) : 0);

/** „Ott hagytad abba: 15. lap, Tripla tető” — csak ha van mentett lap, és nem az első. */
export function resumeText(haladas, tartalom) {
  const i = haladas?.utolso_lap;
  if (!Number.isInteger(i) || i <= 0) return null;
  const lap = (tartalom || []).find(l => l.idx === i);
  return lap ? `Ott hagytad abba: ${i + 1}. lap, ${lap.cim}` : null;
}

/** Az induló lap: a mélylink (1-től számozva), ha érvényes; különben az első. */
export function startIndex(lapParam, n) {
  const k = Number(lapParam);
  return Number.isInteger(k) && k >= 1 && k <= n ? k - 1 : 0;
}

/* ── Jelölés ── */

/** A látható személyes sor; a nyilvános (kijelentkezett) szabad anyagnál nincs személyes példány. */
export const stampLine = (jel, nyilvanos = false) =>
  (nyilvanos ? 'Nyilvános anyag · web3kurzus.hu' : jel ? `Személyes példány · ${jel.email} · ${jel.wm_id} · ${jel.datum}` : 'Személyes példány');

/** A halvány átlós vízjel háttérképe (SVG data URI). */
export function watermarkBg(wmId) {
  const t = String(wmId || '').replace(/[^A-Z0-9-]/g, '');
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='300' height='190'><text x='10' y='110' transform='rotate(-24 150 95)' font-family='Inter,sans-serif' font-size='13' fill='white' fill-opacity='0.045'>${t} · személyes példány</text></svg>`;
  return `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`;
}

/* ── Nagyítás (3. kör, E2) ── a felhasználó saját beállítása, az alaphoz (zoomFor) képest, százalékban. */

export const ZOOM_STEPS = [80, 90, 100, 115, 130, 150, 175, 200];
export const ZOOM_STEPS_MOBILE = [85, 90, 100, 115, 130];
export const MOBILE_MAX = 759;
export const isMobileWidth = (width) => width <= MOBILE_MAX;
export const zoomSteps = (width) => (isMobileWidth(width) ? ZOOM_STEPS_MOBILE : ZOOM_STEPS);

/** A legközelebbi érvényes fokozat (pl. asztalról mentett 175% telefonon 130%). */
export function snapZoom(pct, width) {
  const steps = zoomSteps(width);
  const v = Number(pct);
  if (!Number.isFinite(v)) return 100;
  return steps.reduce((best, s) => (Math.abs(s - v) < Math.abs(best - v) ? s : best), steps[0]);
}

/** Egy fokozattal feljebb (+1) vagy lejjebb (−1); a határon marad. */
export function stepZoom(pct, dir, width) {
  const steps = zoomSteps(width);
  const i = steps.indexOf(snapZoom(pct, width));
  return steps[Math.max(0, Math.min(steps.length - 1, i + dir))];
}

/** Billentyű → +1 / −1 / 0 (csak módosító nélkül; a böngésző Cmd/Ctrl-nagyítása érintetlen). */
export function zoomKey(e) {
  if (e.ctrlKey || e.metaKey || e.altKey) return 0;
  if (e.key === '+' || e.key === '=') return 1;
  if (e.key === '-' || e.key === '−' || e.key === '_') return -1;
  return 0;
}

/* Az eszközön mentődik, anyagtól függetlenül; a telefon és a gép külön kulcson (a szélesség szerint). */
const zoomKeyName = (width) => `w3k_olvaso_nagyitas_${isMobileWidth(width) ? 'mobil' : 'asztal'}`;
export function loadZoom(width, storage = typeof window !== 'undefined' ? window.localStorage : null) {
  try { const v = storage?.getItem(zoomKeyName(width)); return v ? snapZoom(v, width) : 100; } catch { return 100; }
}
export function saveZoom(pct, width, storage = typeof window !== 'undefined' ? window.localStorage : null) {
  try { storage?.setItem(zoomKeyName(width), String(pct)); } catch { /* nem mentődik, de működik */ }
}

/** Az oszlop szélessége asztalon: a 780 px a nagyítással nő, legfeljebb a képernyő széléig (16-16 px margó). */
export const columnWidth = (pct, width) => (isMobileWidth(width) ? null : Math.min(Math.round(780 * (pct / 100)), width - 32));

/** Ha az oszlop elérte a képernyő szélét, a lap nagyítása sem nő tovább (különben a fix szélességű elemek
    vízszintesen kilógnának): a kért nagyítás és a ténylegesen kapott oszlopszélesség aránya (≤ 1). */
export function zoomFit(pct, width) {
  const col = columnWidth(pct, width);
  return col ? Math.min(1, col / (780 * (pct / 100))) : 1;
}

/** A lap stílusa a Shadow DOM-ba: közös + az anyagé + mobilréteg + nagyítás (az alap × a saját fokozat). */
export function shadowStyle(alap, css, mobilCss, width, pct = 100) {
  const z = Math.round(zoomFor(width) * (pct / 100) * zoomFit(pct, width) * 1000) / 1000;
  return `${alap || ''}\n${css || ''}\n${mobilCss || ''}\n.olv-lap{zoom:${z};background:transparent}`
    + '.olv-cimlap h1{font-size:24pt;margin-bottom:2mm}.olv-cimlap .lead{margin-bottom:4mm}'
    + '*{-webkit-user-select:none;user-select:none}@media print{*{display:none!important}}';
}

/* ── A szerver válaszai a felületen ── */

/** A szerver hibakódja → a felület állapota. */
export function lapAllapot(res) {
  if (!res) return 'hiba';
  if (res.html) return 'ok';
  if (res.hiba === 'korlat') return 'korlat';
  if (res.hiba === 'zarolt') return 'zarolt';
  if (res.hiba === 'sorrendi') return 'sorrendi';
  return 'hiba';
}
